import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  LinearProgress,
  Avatar,
  IconButton,
  Tooltip,
  Container,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  LocalPharmacy,
  Inventory,
  Warning,
  Business,
  Phone,
  Email,
  Visibility,
  LocalOffer,
} from "@mui/icons-material";
import { medicinesAPI, suppliersAPI, inventoryAPI } from "../../services/API";
import { useAuth } from "../../contexts/AuthContext";

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`tabpanel-${index}`}
    aria-labelledby={`tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

export const MedicineDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [medicine, setMedicine] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [medicineBatches, setMedicineBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const { hasAnyRole } = useAuth();

  useEffect(() => {
    fetchMedicineDetails();
  }, [id]);

  const fetchMedicineDetails = async () => {
    try {
      // Fetch medicine details and suppliers in parallel
      const [medicineResponse, suppliersResponse] = await Promise.all([
        medicinesAPI.getById(id),
        suppliersAPI.getSuppliersByMedicine(id),
      ]);

      const medicineData =
        medicineResponse.data.data.medicine || medicineResponse.data.data;
      setMedicine(medicineData);

      // Set suppliers from the API response
      const suppliersData =
        suppliersResponse.data.data.suppliers ||
        suppliersResponse.data.data ||
        [];
      setSuppliers(suppliersData);

      // Fetch batches separately with error handling
      try {
        const inventoryResponse = await inventoryAPI.getStockSummary({ medicineId: id });
        const stockData = inventoryResponse.data.data;
        
        let batchesData = [];
        if (stockData.stockSummary && stockData.stockSummary.length > 0) {
          const medicineStock = stockData.stockSummary.find(item => 
            item.medicine && item.medicine._id === id
          );
          batchesData = medicineStock?.batches || [];
        }
        setMedicineBatches(batchesData);
      } catch (error) {
        console.warn("Using empty batches array");
        setMedicineBatches([]);
      }
    } catch (error) {
      console.error("Error fetching medicine details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getScheduleColor = (schedule) => {
    const colors = {
      non_scheduled: "success",
      schedule_h: "warning",
      schedule_h1: "warning",
      schedule_x: "error",
      narcotic: "error",
    };
    return colors[schedule] || "default";
  };

  const getExpiryStatus = (expiryDate) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) return { status: "Expired", color: "error" };
    if (daysUntilExpiry <= 30)
      return { status: "Expiring Soon", color: "warning" };
    return { status: "Valid", color: "success" };
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase();
  };

  const getStockStatus = (stockQty, minStock) => {
    if (stockQty === 0) return { label: "Out of Stock", color: "error" };
    if (stockQty <= minStock) return { label: "Low Stock", color: "warning" };
    return { label: "In Stock", color: "success" };
  };

  // Calculate total stock from all batches
  const totalStock = medicineBatches.reduce(
    (sum, batch) => sum + (batch.stockQty || batch.availableQuantity || 0),
    0
  );

  // Get supplier product details for a specific medicine
  const getSupplierProduct = (supplier, medicineId) => {
    return (
      supplier.medicines?.find(
        (med) => med.medicine && med.medicine._id === medicineId
      ) ||
      supplier.supplierMedicines?.find(
        (med) => med.medicine && med.medicine._id === medicineId
      )
    );
  };

  if (loading) {
    return <LinearProgress />;
  }

  if (!medicine) {
    return <Alert severity="error">Medicine not found</Alert>;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)",
      }}
    >
      <Container maxWidth="xl" sx={{ py: 5 }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate("/hospital/pharmacy/medicines")}
            variant="outlined"
          >
            Back to Medicines
          </Button>
          <Typography variant="h3" fontWeight="bold" sx={{ flexGrow: 1 }}>
            {medicine.name}
          </Typography>
          {hasAnyRole(["admin", "inventory_manager"]) && (
            <Button
              startIcon={<Edit />}
              variant="contained"
              onClick={() => navigate(`/hospital/pharmacy/medicines/${id}/edit`)}
            >
              Edit Medicine
            </Button>
          )}
        </Box>

        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label="Overview" />
          <Tab label="Batches & Stock" />
          <Tab label={`Suppliers (${suppliers.length})`} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12} md={8}>
              <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom fontWeight="bold">
                    Basic Information
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1" color="text.secondary">
                        Generic Name
                      </Typography>
                      <Typography variant="h6" fontWeight="medium">
                        {medicine.genericName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1" color="text.secondary">
                        Brand
                      </Typography>
                      <Typography variant="h6" fontWeight="medium">
                        {medicine.brand}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1" color="text.secondary">
                        Manufacturer
                      </Typography>
                      <Typography variant="h6" fontWeight="medium">
                        {medicine.manufacturer}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1" color="text.secondary">
                        Category & Type
                      </Typography>
                      <Typography variant="h6" fontWeight="medium">
                        {medicine.category} • {medicine.type}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1" color="text.secondary">
                        HSN Code
                      </Typography>
                      <Typography variant="h6" fontWeight="medium">
                        {medicine.hsnCode}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1" color="text.secondary">
                        GST Percent
                      </Typography>
                      <Typography variant="h6" fontWeight="medium">
                        {medicine.gstPercent}%
                      </Typography>
                    </Grid>
                    {medicine.schedule && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body1" color="text.secondary">
                          Schedule
                        </Typography>
                        <Chip
                          label={medicine.schedule
                            .replace("_", " ")
                            .toUpperCase()}
                          size="small"
                          color={getScheduleColor(medicine.schedule)}
                          sx={{ borderRadius: 2 }}
                        />
                      </Grid>
                    )}
                    {medicine.minStock && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body1" color="text.secondary">
                          Minimum Stock
                        </Typography>
                        <Typography variant="h6" fontWeight="medium">
                          {medicine.minStock} units
                        </Typography>
                      </Grid>
                    )}
                    {medicine.description && (
                      <Grid item xs={12}>
                        <Typography variant="body1" color="text.secondary">
                          Description
                        </Typography>
                        <Typography variant="h6">
                          {medicine.description}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Stock & Suppliers Summary */}
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                <CardContent>
                  <Typography
                    variant="h5"
                    gutterBottom
                    fontWeight="bold"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Inventory color="primary" />
                    Stock Summary
                  </Typography>

                  <Box sx={{ textAlign: "center", py: 3 }}>
                    <Typography
                      variant="h2"
                      fontWeight="bold"
                      color="primary.main"
                    >
                      {totalStock}
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      Total Units in Stock
                    </Typography>

                    <Box sx={{ mt: 3 }}>
                      <Chip
                        label={
                          getStockStatus(totalStock, medicine.minStock || 10)
                            .label
                        }
                        color={
                          getStockStatus(totalStock, medicine.minStock || 10)
                            .color
                        }
                        sx={{ mb: 1, borderRadius: 2 }}
                      />
                      <Typography variant="h6" color="text.secondary">
                        {medicineBatches.length} active batches
                      </Typography>
                      {medicine.minStock && (
                        <Typography variant="h6" color="text.secondary">
                          Min Stock: {medicine.minStock}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {totalStock <= (medicine.minStock || 10) && (
                    <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                      Stock is below minimum threshold. Consider placing a
                      purchase order.
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Available Suppliers Summary */}
              <Card sx={{ mt: 3, borderRadius: 3, boxShadow: 2 }}>
                <CardContent>
                  <Typography
                    variant="h5"
                    gutterBottom
                    fontWeight="bold"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Business color="primary" />
                    Available Suppliers
                  </Typography>

                  {suppliers.length === 0 ? (
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      sx={{ textAlign: "center", py: 2 }}
                    >
                      No suppliers found for this medicine
                    </Typography>
                  ) : (
                    <Box>
                      <Typography
                        variant="h3"
                        fontWeight="bold"
                        color="primary.main"
                        sx={{ textAlign: "center", py: 1 }}
                      >
                        {suppliers.length}
                      </Typography>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        sx={{ textAlign: "center", mb: 2 }}
                      >
                        Supplier{suppliers.length !== 1 ? "s" : ""} Available
                      </Typography>

                      <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                        {suppliers.slice(0, 3).map((supplier) => {
                          const supplierProduct = getSupplierProduct(
                            supplier,
                            id
                          );
                          return (
                            <Box
                              key={supplier._id}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                p: 1,
                                mb: 1,
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 2,
                                bgcolor: "background.paper",
                              }}
                            >
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  bgcolor: "primary.main",
                                  fontSize: "0.875rem",
                                }}
                              >
                                {getInitials(supplier.name)}
                              </Avatar>
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="h6" fontWeight="medium">
                                  {supplier.name}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  color="text.secondary"
                                >
                                  {supplierProduct
                                    ? `₹${
                                        supplierProduct.costPrice ||
                                        supplierProduct.purchaseRate
                                      }`
                                    : "Contact for price"}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>

                      {suppliers.length > 3 && (
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          sx={{ textAlign: "center", display: "block", mt: 1 }}
                        >
                          +{suppliers.length - 3} more suppliers
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Batches & Stock Tab Content */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom fontWeight="bold">
                    Batch Details & Stock Information
                  </Typography>
                  {medicineBatches.length === 0 ? (
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      sx={{ textAlign: "center", py: 4 }}
                    >
                      No batches found for this medicine
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Batch Number</TableCell>
                            <TableCell>Supplier</TableCell>
                            <TableCell>Stock Quantity</TableCell>
                            <TableCell>Purchase Rate</TableCell>
                            <TableCell>MRP</TableCell>
                            <TableCell>Expiry Date</TableCell>
                            <TableCell>Stock Status</TableCell>
                            <TableCell>Expiry Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {medicineBatches.map((batch) => {
                            const expiryStatus = getExpiryStatus(
                              batch.expiryDate
                            );
                            const stockStatus = getStockStatus(
                              batch.stockQty || batch.availableQuantity,
                              medicine.minStock || 10
                            );

                            return (
                              <TableRow key={batch._id || batch.batchNumber} hover>
                                <TableCell>
                                  <Typography variant="h6" fontWeight="medium">
                                    {batch.batchNumber}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {batch.supplier ? (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <Avatar
                                        sx={{
                                          width: 32,
                                          height: 32,
                                          fontSize: "0.75rem",
                                          bgcolor: "primary.main",
                                        }}
                                      >
                                        {getInitials(batch.supplier.name)}
                                      </Avatar>
                                      <Box>
                                        <Typography
                                          variant="h6"
                                          fontWeight="medium"
                                        >
                                          {batch.supplier.name}
                                        </Typography>
                                        <Typography
                                          variant="body1"
                                          color="text.secondary"
                                        >
                                          {batch.supplier.contactPerson}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  ) : (
                                    <Typography
                                      variant="h6"
                                      color="text.secondary"
                                    >
                                      No supplier
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="h6" fontWeight="medium">
                                    {batch.stockQty || batch.availableQuantity}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="h6">
                                    ₹{batch.costPrice || batch.purchaseRate}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="h6" fontWeight="medium">
                                    ₹{batch.sellingPrice || batch.mrp}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="h6">
                                    {new Date(
                                      batch.expiryDate
                                    ).toLocaleDateString()}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={stockStatus.label}
                                    size="small"
                                    color={stockStatus.color}
                                    sx={{ borderRadius: 2 }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={expiryStatus.status}
                                    size="small"
                                    color={expiryStatus.color}
                                    sx={{ borderRadius: 2 }}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* Suppliers Tab Content */}
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                Available Suppliers ({suppliers.length})
              </Typography>
              {suppliers.length === 0 ? (
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ textAlign: "center", py: 4 }}
                >
                  No suppliers found for this medicine
                </Typography>
              ) : (
                <Grid container spacing={3}>
                  {suppliers.map((supplier) => {
                    const supplierProduct = getSupplierProduct(supplier, id);

                    return (
                      <Grid item xs={12} md={6} lg={4} key={supplier._id}>
                        <Card
                          variant="outlined"
                          sx={{
                            height: "100%",
                            borderRadius: 3,
                            transition: "0.3s",
                            "&:hover": {
                              transform: "translateY(-4px)",
                              boxShadow: 4,
                            },
                          }}
                        >
                          <CardContent>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mb: 2,
                              }}
                            >
                              <Avatar
                                sx={{
                                  bgcolor: "primary.main",
                                  width: 56,
                                  height: 56,
                                }}
                              >
                                {getInitials(supplier.name)}
                              </Avatar>
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="h5" fontWeight="bold">
                                  {supplier.name}
                                </Typography>
                                <Typography
                                  variant="h6"
                                  color="text.secondary"
                                >
                                  {supplier.contactPerson}
                                </Typography>
                              </Box>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  mb: 1,
                                }}
                              >
                                <Phone fontSize="small" color="action" />
                                <Typography variant="h6">
                                  {supplier.phone}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  mb: 1,
                                }}
                              >
                                <Email fontSize="small" color="action" />
                                <Typography variant="h6">
                                  {supplier.email}
                                </Typography>
                              </Box>
                              {supplier.address && (
                                <Typography
                                  variant="h6"
                                  color="text.secondary"
                                  sx={{ mt: 1, fontSize: "0.875rem" }}
                                >
                                  {typeof supplier.address === "string"
                                    ? supplier.address
                                    : `${supplier.address.street || ""} ${
                                        supplier.address.city || ""
                                      } ${supplier.address.state || ""} ${
                                        supplier.address.zipCode || ""
                                      }`.trim()}
                                </Typography>
                              )}
                            </Box>

                            {/* Supplier-specific medicine pricing */}
                            {supplierProduct && (
                              <Box
                                sx={{
                                  mb: 2,
                                  p: 2,
                                  bgcolor: "background.default",
                                  borderRadius: 2,
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  gutterBottom
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <LocalOffer fontSize="small" />
                                  Pricing & Terms
                                </Typography>
                                <Grid container spacing={1}>
                                  <Grid item xs={6}>
                                    <Typography
                                      variant="body1"
                                      color="text.secondary"
                                    >
                                      Purchase Rate
                                    </Typography>
                                    <Typography variant="h6" fontWeight="bold">
                                      ₹
                                      {supplierProduct.costPrice ||
                                        supplierProduct.purchaseRate}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography
                                      variant="body1"
                                      color="text.secondary"
                                    >
                                      MRP
                                    </Typography>
                                    <Typography variant="h6">
                                      ₹{supplierProduct.mrp}
                                    </Typography>
                                  </Grid>
                                  {supplierProduct.packSize && (
                                    <Grid item xs={6}>
                                      <Typography
                                        variant="body1"
                                        color="text.secondary"
                                      >
                                        Pack Size
                                      </Typography>
                                      <Typography variant="h6">
                                        {supplierProduct.packSize}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {supplierProduct.discountPercent && (
                                    <Grid item xs={6}>
                                      <Typography
                                        variant="body1"
                                        color="text.secondary"
                                      >
                                        Discount
                                      </Typography>
                                      <Typography variant="h6">
                                        {supplierProduct.discountPercent}%
                                      </Typography>
                                    </Grid>
                                  )}
                                </Grid>
                              </Box>
                            )}

                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                flexWrap: "wrap",
                                mb: 2,
                              }}
                            >
                              {supplier.gstNumber && (
                                <Chip
                                  label={`GST: ${supplier.gstNumber}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ borderRadius: 2 }}
                                />
                              )}
                              {supplier.panNumber && (
                                <Chip
                                  label={`PAN: ${supplier.panNumber}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ borderRadius: 2 }}
                                />
                              )}
                              <Chip
                                label={supplier.isActive ? "Active" : "Inactive"}
                                size="small"
                                color={supplier.isActive ? "success" : "default"}
                                sx={{ borderRadius: 2 }}
                              />
                            </Box>

                            <Button
                              fullWidth
                              variant="outlined"
                              size="small"
                              startIcon={<Visibility />}
                              onClick={() =>
                                navigate(`/hospital/pharmacy/suppliers/${supplier._id}`)
                              }
                              sx={{ borderRadius: 2 }}
                            >
                              View Supplier Details
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </CardContent>
          </Card>
        </TabPanel>
      </Container>
    </Box>
  );
};